import React, { useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { CompanyListCompanyShortDto, CompanyListDto } from 'models/dtoTypes';
import axios from 'axios';
import { get } from 'lodash';
import { date2str } from 'utils/date';
import { styled } from '@mui/material/styles';
import ResourceDetailContainer, { ITab } from 'kosmos/components/ResourceDetailContainer';
import Notice from 'kosmos/components/Notice';
// import Link from 'components/Link';
import { GetCompanyList, UpdateCompanyList } from 'services/companyList';
import CompanyListVerificationTable from './_companyListVerificationTable';
import CompanyListEditTable from './_companyListEditTable';
import generateNonce from 'utils/generateNonce';
import VerificationStatusChip from './_verificationStatusChip';
import { getCountryLabel, countryOptions } from 'utils/countries';
import { IColumn } from 'kosmos/components/ResourceTable';
import { getKosmosReportLink } from 'utils/report';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';

const MINUTE = 60 * 1000;
const getLocalTime = (time: string) =>
  new Date(+new Date(time) - new Date().getTimezoneOffset() * MINUTE);

const PREFIX = 'CompanyListDetailPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export interface ICountryOption {
  label: string;
  alpha3: string;
}

function CompanyListDetailPage(props: RouteComponentProps<{ id: string }>): JSX.Element {
  const id = props.match.params.id;
  const [openCreatedNotice, setOpenCreatedNotice] = React.useState(false);
  const [tabId, setTabId] = React.useState('details');
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [countries, setCountries] = React.useState<ICountryOption[]>([]);
  const { t } = useTranslation('default');

  const fetchCountries = () => {
    const options = countryOptions();
    let formattedOptions = options.map((item) => ({
      label: item.label || '',
      alpha3: item.alias || '',
    }));
    formattedOptions = formattedOptions.filter((a) => a.alpha3 !== 'world');
    setCountries(formattedOptions);
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleFetch = async (): Promise<CompanyListDto> => {
    const companyList = await GetCompanyList(id);
    return companyList;
  };

  const handleCloseCreatedNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenCreatedNotice(false);
  };

  const generateCompanyColumns = (): IColumn<CompanyListCompanyShortDto>[] => {
    return [
      {
        label: t(''),
        sortIndex: '',
        render: () => '',
      },
      {
        label: t('input-name'),
        sortIndex: 'name',
        render: (item) => item.name || '',
      },
      {
        label: t('company-number'),
        render: (item) => item.registrationNumbers || '',
      },
      {
        label: t('external-identifier'),
        render: (item) => {
          if (item.company?.externalIdentifiers && item.company?.externalIdentifiers?.length) {
            return (
              <div>
                {item.company?.externalIdentifiers.map((aReg) => (
                  <>
                    {aReg.typeDescription}: {aReg.identifier}
                    <br />
                  </>
                ))}
              </div>
            );
          }
          return '';
        },
      },
      {
        label: t('supplier-name'),
        render: (item) => item.company?.name || '',
      },
      {
        label: t('country'),
        dataIndex: 'countryCode',
        render: (item) => getCountryLabel(item.countryCode || ''),
      },
      {
        label: t('status'),
        render: (item) => (
          <VerificationStatusChip
            verificationStatus={item.verificationStatus}
            isDissolved={item.company?.statusInfo === 'Dissolved'}
            scoringState={item.scoringState}
          />
        ),
      },
      {
        label: t('score'),
        render: (item) => {
          const completed = item.scoringState === 'Completed';
          if (completed) {
            return (
              <span>
                <a
                  href={getKosmosReportLink(item.company?.id, null, item?.reportSnapshotId)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.score}
                </a>
              </span>
            );
          } else {
            return 'N/A';
          }
        },
      },
    ];
  };
  const generateTabs = (): ITab<CompanyListDto>[] => [
    {
      id: 'details',
      title: t('details'),
      getUiSchema: () => ({
        fields: [
          {
            label: t('id'),
            name: 'id',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('name'),
            name: 'name',
            widget: 'text',
          },
          {
            label: t('description'),
            name: 'description',
            widget: 'text',
          },
          {
            label: t('Pending/Active'),
            name: 'isPending',
            formatter: (isPending: boolean) => (
              <Chip
                label={isPending? t('Waiting') : t('Active')}
                color={isPending ? 'default' : 'primary' }
              />
            ),
             widget: 'text',
            readonly: true,
          },
          {
            label: t('last-score-triggered-at'),
            formatter: (datestr: string) => {
              return datestr == '0001-01-01T00:00:00' || !datestr
                ? t('not-triggered-yet')
                : date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm');
            },
            name: 'lastTriggeredAt',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('Last Score At'),
            formatter: (datestr: string) => {
              return datestr == '0001-01-01T00:00:00' || !datestr
                ? ''
                : date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm');
            },
            name: 'lastTriggeredAt',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('created-at'),
            formatter: (datestr: string) => date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm'),
            name: 'created',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('last-updated-at'),
            name: 'updated',
            formatter: (datestr: string) => date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm'),
            readonly: true,
            widget: 'text',
          },
        ],
      }),
    },
    {
      id: 'companies',
      title: t('suppliers'),
      render: (companyList: CompanyListDto) => {
        return (
          <div>
            <CompanyListEditTable
              countries={countries}
              companyListId={companyList.id}
              onErrorResponse={handleError}
            />
          </div>
        );
      },
    },
    {
      id: 'verifiedCompanies',
      title: t('scoring-results'),
      render: (companyList: CompanyListDto) => {
        return (
          <div>
            <CompanyListVerificationTable
              countries={countries}
              onErrorResponse={handleError}
              columns={generateCompanyColumns()}
              companyListId={companyList.id}
            />
          </div>
        );
      },
    },
  ];

  const handleUpdate = async (attrs: {
    name?: string;
    display?: string;
  }): Promise<CompanyListDto> => {
    const org = await UpdateCompanyList(id, attrs);
    setNonce(generateNonce());
    return org;
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      // console.log(e.response?.data);
      const errorMessage =
        get(e.response?.data, 'message') ||
        get(e.response?.data, 'title') ||
        t('something-went-wrong');
      setOpenCreatedNotice(true);
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setOpenCreatedNotice(true);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  return (
    <Root>
      <ResourceDetailContainer
        key={nonce}
        tabs={generateTabs()}
        breadcrumbText={t('company-lists')}
        breadcrumbUrl="/companyLists"
        onTabChanged={setTabId}
        onFetch={handleFetch}
        onUpdate={handleUpdate}
        currentTabId={tabId}
      />
      {noticeData && (
        <Notice
          open={openCreatedNotice}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseCreatedNotice}
        />
      )}
    </Root>
  );
}

export default withRouter(CompanyListDetailPage);
